import { Box, Divider, Fade, Paper, Typography } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import {
	CountingType,
	QuotaType,
	TandemLicenseInfo,
	Version00TandemLicenseInfo,
	Version01TandemLicenseInfo,
} from '../../api/clients/tandem/DTOs'
import TandemClient from '../../api/clients/tandem/TandemClient'
import { FullscreenSpinner } from '../../components/feedback/circular'
import { PropertiesList, Property } from '../../components/lists/PropertiesList'
import AdministrationPageContainer from '../Organization/Administration/AdministrationPageContainer'
import { AdministrationPageTabs } from '../Organization/Administration/AdministrationPageTabs'

type TandemLicensePageProps = {
	tabLabels: string[]
	onTabChanged: (event: React.SyntheticEvent, value: number) => void
}

export const TandemLicensePage = (props: TandemLicensePageProps) => {
	const tandemClient = new TandemClient()

	const [licenseInfo, setLicenseInfo] = useState<TandemLicenseInfo>()

	useEffect(() => {
		tandemClient.getLicenseInfo().then((response) => {
			setLicenseInfo(response.data)
		})
	}, [])

	if (licenseInfo === undefined) return <FullscreenSpinner />

	return (
		<AdministrationPageContainer title="Tandem License">
			<Box display="flex" flexDirection="column" width="100%" height="100%">
				<AdministrationPageTabs
					currentTab={0}
					onTabChanged={props.onTabChanged}
					tabLabels={props.tabLabels}
				/>
				<Divider />
				<Fade in>
					<Box>
						{licenseInfo.type === 'Version00' && (
							<Version00LicenseDisplay licenseInfo={licenseInfo} />
						)}
					</Box>
				</Fade>
			</Box>
		</AdministrationPageContainer>
	)
}

type Version00LicenseDisplayProps = {
	licenseInfo: Version00TandemLicenseInfo
}

const Version00LicenseDisplay = ({
	licenseInfo,
}: Version00LicenseDisplayProps) => {
	const properties: Property[] = useMemo(() => {
		return [
			{
				label: 'Start Date',
				value: new Date(licenseInfo.startDate).toLocaleDateString(undefined, {
					year: 'numeric',
					month: 'long',
					day: 'numeric',
				}),
			},
			{
				label: 'Expiration Date',
				value:
					licenseInfo.expirationDate !== undefined &&
					new Date(licenseInfo.expirationDate).toLocaleDateString(undefined, {
						year: 'numeric',
						month: 'long',
						day: 'numeric',
					}),
			},

			{
				label: 'Quota',
				value: `${licenseInfo.quotaInfo.quota} ${
					licenseInfo.quotaType === QuotaType.Fields ? 'Fields' : 'Pages'
				}`,
			},
			{
				label: 'Used',
				value: `${licenseInfo.quotaInfo.used} ${
					licenseInfo.quotaType === QuotaType.Fields ? 'Fields' : 'Pages'
				}`,
			},
			{
				label: 'Counting Type',
				value:
					licenseInfo.quotaInfo.countingType === CountingType.AllTime
						? 'All Time'
						: 'Annual',
			},
			{
				label: 'Exceeded',
				value: licenseInfo.quotaInfo.exceeded ? (
					<Typography variant="subtitle2" color="error.main">
						true
					</Typography>
				) : (
					'false'
				),
			},
		]
	}, [licenseInfo])

	return (
		<Box component={Paper} marginY={3}>
			<PropertiesList properties={properties} />
		</Box>
	)
}

type Version01LicenseDisplayProps = {
	licenseInfo: Version01TandemLicenseInfo
}
